import { Button, Flex, Icon, Text, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { AlertTriangle } from 'lucide-react';

import { NatReportRules, NatReportTitles } from '@duke-hq/avetmiss/NATRules';
import type { NatFileType } from '@duke-hq/avetmiss/NATTypes';
import { createNatFileHandler } from '@duke-hq/avetmiss/NATHelpers';

import { NATReportTable, type CrossFileReferenceForRow } from './NATReportTable';
import { useNATCache } from './hooks/useNATCache';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export type NATComponentProps = {
  natFileId: NatFileType;
  title?: string;
  fromDate: string;
  toDate: string;
  validationAvailable?: boolean;
  validationErrorCount?: number;
  validationWarningCount?: number;
  validationErrorSourceRowIndices?: number[];
  validationWarningSourceRowIndices?: number[];
  validationReferences?: CrossFileReferenceForRow[];
  initialFilter?: string;
};

export function NATComponent({
  natFileId,
  title,
  fromDate,
  toDate,
  validationAvailable,
  validationErrorCount,
  validationWarningCount,
  validationErrorSourceRowIndices,
  validationWarningSourceRowIndices,
  validationReferences,
  initialFilter,
}: NATComponentProps) {
  const toast = useToast();
  const natCache = useNATCache();

  const status = natCache.getStatus(natFileId);
  const timing = natCache.getTiming(natFileId);
  const isFetching = status === 'loading';
  const hasError = status === 'error';

  const triggerGeneration = natCache.triggerGeneration;

  useEffect(() => {
    if (fromDate.length === 0 || toDate.length === 0) {
      return;
    }
    if (status !== 'idle') {
      return;
    }
    void triggerGeneration(natFileId);
  }, [triggerGeneration, natFileId, fromDate, toDate, status]);

  const cachedData = useMemo(() => {
    return natCache.getCachedData(natFileId, fromDate, toDate);
  }, [natCache, natFileId, fromDate, toDate]);

  const columns = useMemo(() => {
    const natRules = NatReportRules[natFileId];
    return natRules ?? {};
  }, [natFileId]);

  const componentTitle = useMemo(() => {
    if (title != null && title.length > 0) {
      return title;
    }

    return NatReportTitles[natFileId] ?? `${natFileId} File`;
  }, [title, natFileId]);

  const handleCreateNatFile = useCallback(
    async (fileData: { rows: Record<string, string>[] }) => {
      try {
        await createNatFileHandler(natFileId)({ rows: fileData.rows });
        toast({
          description: t`NAT file ${natFileId}.txt created successfully.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          description:
            error instanceof Error ? error.message : t`Failed to create NAT file ${natFileId}.txt.`,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [natFileId, toast],
  );

  const handleRegenerate = useCallback(() => {
    void natCache.triggerGeneration(natFileId, { force: true });
  }, [natCache, natFileId]);

  if (hasError) {
    return (
      <Flex direction="column" justify="center" align="center" minH="200px" gap={3}>
        <Flex align="center" gap={2} color="fg.muted">
          <Icon as={AlertTriangle} boxSize={iconSize.md} />
          <Text fontSize="sm">
            <Trans>Something went wrong generating this report.</Trans>
          </Text>
        </Flex>
        <Button size="sm" variant="outline" onClick={handleRegenerate}>
          <Trans>Try again</Trans>
        </Button>
      </Flex>
    );
  }

  const generatedInMs =
    status === 'done' && timing != null && timing.completedAt != null
      ? timing.completedAt - timing.startedAt
      : undefined;

  return (
    <NATReportTable
      title={componentTitle}
      columns={columns}
      response={cachedData ?? undefined}
      isFetching={isFetching}
      createNatFile={handleCreateNatFile}
      natFileId={natFileId}
      generatedInMs={generatedInMs}
      onRegenerate={handleRegenerate}
      crossFileValidationAvailable={validationAvailable}
      crossFileErrorCount={validationErrorCount}
      crossFileWarningCount={validationWarningCount}
      crossFileErrorSourceRowIndices={validationErrorSourceRowIndices}
      crossFileWarningSourceRowIndices={validationWarningSourceRowIndices}
      crossFileReferences={validationReferences}
      initialFilter={initialFilter}
    />
  );
}
